import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { FormikActions } from 'formik'
import {
  ProvingDetailsAccomodationForm,
  ProvingDetailsAccomodationFormSchema,
  ProvingDetailsFerryForm,
  ProvingDetailsOrderIdForm,
  ProvingDetailsParticipantReassignForm,
  ProvingDetailsRentalCarForm,
  ProvingDetailsTimespanForm,
  ProvingDetailsTrackHotelForm,
  ProvingDetailsTrackHotelFormSchema,
} from 'forms/ProvingDetailsForm'
import { ProvingDetailsNordschleifeForm } from 'forms/ProvingDetailsForm/participants/ProvingDetailsNordschleifeForm'
import { ProvingDetailsTrackHotelOutwardStatusForm } from 'forms/ProvingDetailsForm/participants/ProvingDetailsTrackHotelOutwardStatusForm'
import { ProvingDetailsTrackHotelReturnStatusForm } from 'forms/ProvingDetailsForm/participants/ProvingDetailsTrackHotelReturnStatusForm'
import {
  AccomodationType,
  BookingStatus,
  CarType,
  FlightType,
  Participant,
  ParticipantBatchInput,
  ParticipantUpdateInput,
  UserRole,
} from 'gql.generated'
import { generateNestedGroups, omitDeep, removeNullProperties } from 'helpers'
import {
  useCommand,
  useCustomSearchCommand,
  useDeselectCommand,
  useShowCommand,
} from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import _ from 'lodash'
import moment from 'moment'
import { PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { useReadParticipantsQuery } from 'views/ParticipantViews/DayOfArrivalView/DayOfArrivalView.generated'
import { useUpdateParticipantsMutation } from 'views/ProvingDetailsView/hooks/PageParticipants.generated'
import { ParticipantListByStartDateNestedGrouping } from './ParticipantListByStartDateNestedGrouping'

enum FormType {
  timespan,
  nordschleife,
  orderId,
  accomodation,
  rentalCar,
  ferry,
  hotels,
  hotelsOutwardStatus,
  hotelsReturnStatus,
}

export type ParticipantListItem = Participant & {
  provingGround?: string
  provingGroundId?: any
  proving?: any
  provingId?: string
  provingDateFrom?: string
  provingDateTo?: string
  provingTitel?: string
  provingCreator?: string
  provingOrderIds?: any
  department?: string
  groupKey?: string
}

const getParticipants = (provings: any): ParticipantListItem[] => {
  const participants: ParticipantListItem[] = []
  provings.forEach((proving: any) => {
    const currentParticipants = proving.participants

    currentParticipants.forEach((participant: Participant) => {
      const item: ParticipantListItem = participant
      item.provingTitel = proving.title
      item.proving = proving.id
      item.provingId = proving._id
      item.provingDateFrom = proving.dateFrom
      item.provingDateTo = proving.dateTo
      item.provingGround = proving.provingGround.title
      item.provingGroundId = proving.provingGround._id
      item.provingCreator = proving.createdBy.name
      item.provingOrderIds = proving.orderIdDistribution
      item.department = proving.department
      participants.push(item)
    })
  })
  return participants
}

const ParticipantsByStartDateListView: React.FC = () => {
  const { t } = useTranslation()
  const [formState, setFormState] = React.useState()

  const [reassignPanelState, showReassignPanel, hideReassignPanel] =
    useFormikPanelState()

  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()

  const [updatePanelState, showUpdatePanel, hideUpdatePanel] =
    useFormikPanelState()

  const [
    updateOrderIdPanelState,
    showOrderIdUpdatePanel,
    hideOrderIdUpdatePanel,
  ] = useFormikPanelState()

  const [accomodationPanelState, showAccomodationPanel, hideAccomodationPanel] =
    useFormikPanelState()

  const [rentalCarPanelState, showRentalCarPanel, hideRentalCarPanel] =
    useFormikPanelState()

  const [ferryPanelState, showFerryPanel, hideFerryPanel] =
    useFormikPanelState()

  const [hotelsPanelState, showHotelsPanel, hideHotelsPanel] =
    useFormikPanelState()

  const [
    hotelsReturnStatusPanelState,
    showHotelsReturnStatusPanel,
    hideHotelsReturnStatusPanel,
  ] = useFormikPanelState()
  const [
    hotelsOutwardStatusPanelState,
    showHotelsOutwardStatusPanel,
    hideHotelsOutwardStatusPanel,
  ] = useFormikPanelState()

  const router = useRouter()

  const [routeParams, updateRouteParams] = useRouteParams<{
    search?: string
  }>('provings', {
    search: undefined,
  })

  const showProving = React.useCallback(
    (proving: Partial<ParticipantListItem>) => {
      router.navigate('provings.details', { id: proving.proving })
    },
    [router]
  )

  const { loading, data, refetch, error } = useReadParticipantsQuery({
    variables: {
      search: routeParams.search,
    },
  })

  const participants = React.useMemo(() => {
    return data && data.provings ? getParticipants(data.provings) : []
  }, [data])
  const [selection, selectedItems] = useSelection<ParticipantListItem>({
    getKey: (item) => item._id,
  })
  const groupingCallbacks = React.useMemo(
    () => [
      (elem: ParticipantListItem) => elem.provingGround,
      (elem: ParticipantListItem) => new Date(elem.dateFrom).getFullYear(),
      (elem: ParticipantListItem) => moment(elem.dateFrom).format('YYYY-MM'),
      (elem: ParticipantListItem) => moment(elem.dateFrom).format('DD.MM.YYYY'),
      (elem: ParticipantListItem) =>
        `${elem.proving},
       ${elem.provingTitel},
       ${elem.provingGround}
      `,
    ],
    []
  )
  const [groupsT, itemsT] = React.useMemo(() => {
    return generateNestedGroups(participants, groupingCallbacks)
  }, [groupingCallbacks, participants])

  const [updateParticipants] = useUpdateParticipantsMutation()
  const disableControl = () => {
    if (selectedItems.length === 0) {
      return true
    }
    let count: number = 0
    const selectedProvings = _.groupBy(selectedItems, 'provingId')
    _.forEach(selectedProvings, (value, key): any => {
      count++
    })

    if (count !== 1) {
      return true
    } else {
      return false
    }
  }

  //#region General Commands
  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 10,
      onClick: () => showProving(selectedItems[0]),
      disabled: disableControl(),
    },
    [selectedItems]
  )
  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.proving.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => {
        updateRouteParams({ search: undefined })
      },
    },
    [routeParams, updateRouteParams]
  )
  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems(selection.getItems(), true),
    },
    [selectedItems]
  )
  //#endregion
  //#region PARTICIPANT
  useCommand(
    {
      key: 'addParticipant',
      priority: 10,
      text: t('model.proving.participant.addParticipant'),
      iconProps: { iconName: 'Contact' },
      disabled: disableControl(),
      subMenuProps: {
        items: [
          // {
          //   key: 'knownParticipant',
          //   text: t('model.proving.participant.participantIsKnown'),
          //   onClick: () => {
          //     showPanel({
          //       personType: '',
          //       participants: [],
          //     })
          //     setFormState({ formType: FormType.addParticipant })
          //   },
          // },
          // {
          //   key: 'unknownParticipant',
          //   text: t('model.proving.participant.participantIsUnknown'),
          //   onClick: () => {
          //     showUnkownParticipantPanel({ amount: 1 })
          //   },
          // },
          // {
          //   key: 'addWorker',
          //   text: t('model.proving.participant.participantWorker'),
          //   onClick: () => {
          //     showWorkerPanel({
          //       personType: 'worker',
          //       participants: [],
          //     })
          //   },
          // },
          // {
          //   key: 'addSupplier',
          //   text: t('model.proving.participant.participantSupplier'),
          //   onClick: () => {
          //     showSupplierPanel({
          //       personType: 'supplier',
          //       participants: [],
          //     })
          //   },
          // },
          {
            key: 'reassignParticipant',
            text: t('model.proving.participant.assignParticipant'),
            disabled: disableControl(),
            onClick: () => {
              showReassignPanel()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleReassignSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          personType: values.personType,
          personId: values.personId,
        },
      }))

      await updateParticipants({
        variables: { provingId: selectedItems[0].provingId as any, batch },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      hideReassignPanel()
      await refetch()
    },
    [selectedItems, hideReassignPanel, selection, refetch, updateParticipants]
  )
  //#endregion PARTICIPANT
  //#region TIMESPAN

  useCommand(
    {
      key: 'timespan',
      priority: 20,
      disabled: disableControl(),
      text: t('field.timespan.label'),
      iconProps: { iconName: 'CalendarWeek' },
      subMenuProps: {
        items: [
          {
            key: 'setTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              if (selectedItems.length > 1) {
                showTimespanPanel()
              } else {
                showTimespanPanel({
                  dateTo: selectedItems[0].dateTo,
                  dateFrom: selectedItems[0].dateFrom,
                })
              }
              setFormState({ formType: FormType.timespan })
            },
          },
          {
            key: 'setDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setTimespanDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )
  const handleTimespanSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)

      const updatedValues = removeNullProperties({
        ...values,
      })
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: updatedValues,
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId!,
          batch,
        },
      })
      await refetch()
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      hideTimespanPanel()
    },
    [selectedItems, updateParticipants, refetch, hideTimespanPanel, selection]
  )

  const setTimespanDateToProvingDate = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: sel.provingDateFrom,
        dateTo: sel.provingDateTo,
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])
  //#endregion TIMESPAN
  //#region ORDERID

  useCommand(
    {
      key: 'orderId',
      priority: 30,
      disabled: disableControl(),
      text: t('model.proving.participant.orderId'),
      iconProps: { iconName: 'DonutChart' },
      subMenuProps: {
        items: [
          {
            key: 'assignOrderId',
            text: t('field.orderId.assignOrderId'),
            onClick: () => {
              showOrderIdUpdatePanel()
              setFormState({ formType: FormType.orderId })
            },
          },
          {
            key: 'noOrderId',
            text: t('field.orderId.noOrderId'),
            onClick: () => {
              setNoOrderId()
            },
          },
        ],
      },
    },
    [selectedItems]
  )
  const setNoOrderId = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        orderId: '',
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleOrderIdUpdate = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: values,
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      await refetch()
      hideOrderIdUpdatePanel()
    },
    [
      selectedItems,
      updateParticipants,
      hideOrderIdUpdatePanel,
      refetch,
      selection,
    ]
  )

  //#endregion ORDERID

  //#region FLIGHT
  useCommand(
    {
      key: 'flight',
      priority: 40,
      disabled: disableControl(),
      text: t('model.proving.participant.flight'),
      iconProps: { iconName: 'Airplane' },
      subMenuProps: {
        items: [
          {
            key: 'roundTripFlight',
            text: t('enum.flight.flightType.roundtrip'),
            onClick: () => {
              handleRoundtripFlightSubmit()
            },
          },
          {
            key: 'outboundFlight',
            text: t('enum.flight.flightType.outboundFlight'),
            onClick: () => {
              handleOutboundFlightSubmit()
            },
          },
          {
            key: 'returnFlight',
            text: t('enum.flight.flightType.returnFlight'),
            onClick: () => {
              handleReturnFlightSubmit()
            },
          },
          {
            key: 'noFlight',
            text: t('enum.flight.flightType.noFlightText'),
            onClick: () => {
              handleNoFlightSubmit()
            },
          },
          {
            key: 'setFlightStatus',
            text: t('field.status.changeStatus.label'),
            subMenuProps: {
              items: [
                {
                  key: 'flightBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleFlightBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'flightBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleFlightBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'flightBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleFlightBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'flightBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleFlightBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [setFormState, selectedItems, refetch]
  )
  const handleNoFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.NoFlight,
          bookingStatus: BookingStatus.NotRequired,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleReturnFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.ReturnFlight,
          bookingStatus: BookingStatus.Open,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleOutboundFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.OutboundFlight,
          bookingStatus: BookingStatus.Open,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleRoundtripFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.Roundtrip,
          bookingStatus: BookingStatus.Open,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          flight: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId!,
          batch,
        },
      })
      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId!,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          flight: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId!,
          batch,
        },
      })
      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          flight: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId!,
          batch,
        },
      })
      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  //#endregion FLIGHT

  //#region ACCOMODATION

  useCommand(
    {
      key: 'accomodation',
      priority: 50,
      disabled: disableControl(),
      text: t('model.proving.participant.accomodation'),
      iconProps: { iconName: 'Hotel' },
      subMenuProps: {
        items: [
          {
            key: 'setAccomodation',
            text: t('field.accomodation.setAccomodation.label'),
            onClick: () => {
              const accomodation = omitDeep(
                selectedItems[0].accomodation,
                '__typename'
              )
              if (selectedItems.length > 1) {
                showAccomodationPanel()
              } else {
                showAccomodationPanel({
                  accomodation: {
                    dateTo: accomodation.dateTo,
                    dateFrom: accomodation.dateFrom,
                    accomodationType: accomodation.accomodationType,
                    accomodation: accomodation.accomodation
                      ? accomodation.accomodation._id
                      : '',
                    bookingStatus: accomodation.bookingStatus,
                  },
                })
              }
              setFormState({ formType: FormType.accomodation })
            },
          },
          {
            key: 'noAccomodation',
            text: t('field.accomodation.noAccomodation'),
            onClick: () => {
              handleNoAccomodation()
            },
          },
          {
            key: 'setAccomodationStatus',
            text: t('field.status.changeStatus.label'),
            subMenuProps: {
              items: [
                {
                  key: 'accomodationBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleAccomodationBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'accomodationBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleAccomodationBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'accomodationBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleAccomodationBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'accomodationBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleAccomodationBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleNoAccomodation = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        accomodation: {
          accomodation: null,
          accomodationType: AccomodationType.NoAccomodation,
          dateFrom: null,
          dateTo: null,
          bookingStatus: BookingStatus.NotRequired,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })

    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })

      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusOpenSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.Open,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })

      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })

      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })

      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationsSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      let newValues: ParticipantUpdateInput = removeNullProperties({
        ...values,
      })
      if (
        newValues.accomodation!.bookingStatus === BookingStatus.NotRequired &&
        newValues.accomodation!.accomodationType ===
          AccomodationType.AccomodationWithPreference
      ) {
        newValues!.accomodation!.bookingStatus = BookingStatus.Open
      } else {
        newValues = values
      }

      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: newValues,
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      await refetch()
      hideAccomodationPanel()
    },
    [
      selectedItems,
      updateParticipants,
      hideAccomodationPanel,
      refetch,
      selection,
    ]
  )
  //#endregion ACCOMODATION

  //#region RENTALCAR
  useCommand(
    {
      key: 'rentalCar',
      priority: 60,
      disabled: disableControl(),
      text: t('model.proving.participant.rentalCar'),
      iconProps: { iconName: 'Car' },
      subMenuProps: {
        items: [
          {
            key: 'setRentalCar',
            text: t('field.rentalCar.setRentalCar.label'),
            onClick: () => {
              const rentalCar = omitDeep(
                selectedItems[0].rentalCar,
                '__typename'
              )
              if (selectedItems.length > 1) {
                showRentalCarPanel()
              } else {
                showRentalCarPanel({
                  rentalCar: {
                    ...rentalCar,
                    carType:
                      rentalCar && rentalCar.carType ? rentalCar.carType : '',
                  },
                })
              }
              setFormState({ formType: FormType.rentalCar })
            },
          },
          {
            key: 'noRentalCar',
            text: t('field.rentalCar.noRentalCar'),
            onClick: () => {
              handleNoRentalCar()
            },
          },
          {
            key: 'setRentalCarStatus',
            text: t('field.status.changeStatus.label'),
            subMenuProps: {
              items: [
                {
                  key: 'rentalCarBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleRentalCarBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleRentalCarBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleRentalCarBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleRentalCarBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleRentalCarBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        rentalCar: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))
      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleNoRentalCar = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        rentalCar: {
          carType: CarType.None,
          bookingStatus: BookingStatus.NotRequired,
          dateFrom: null,
          dateTo: null,
        },
      },
    }))
    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)

      let newValues: ParticipantUpdateInput = values

      if (
        newValues.rentalCar!.bookingStatus === BookingStatus.NotRequired &&
        newValues.rentalCar!.carType !== CarType.None
      ) {
        newValues!.rentalCar!.bookingStatus = BookingStatus.Open
      } else {
        newValues = values
      }

      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: newValues,
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      await refetch()
      hideRentalCarPanel()
    },
    [selectedItems, updateParticipants, hideRentalCarPanel, refetch, selection]
  )

  //#endregion RENTALCAR

  //#region NORDSCHLEIFE
  useCommand(
    {
      key: 'nordschleife',
      priority: 70,
      text: t('field.nordschleife.label'),
      disabled: disableControl(),
      iconProps: { iconName: 'Ellipse' },
      onClick: () => {
        showUpdatePanel()
        setFormState({ formType: FormType.nordschleife })
      },
    },
    [selectedItems]
  )

  const handleUpdateSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: { nordschleife: values.nordschleife },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      await refetch()
      hideUpdatePanel()
    },
    [selectedItems, updateParticipants, hideUpdatePanel, refetch, selection]
  )
  //#endregion NORDSCHLEIFE

  //#region FERRY
  useCommand(
    {
      key: 'ferry',
      priority: 70,
      text: t('field.ferry.label'),
      disabled: disableControl(),
      iconProps: { iconName: 'Ferry' },
      subMenuProps: {
        items: [
          {
            key: 'setFerry',
            text: t('field.ferry.setFerry.label'),
            onClick: () => {
              if (selectedItems.length === 1) {
                const ferry = omitDeep(selectedItems[0].ferry, '__typename')
                showFerryPanel({
                  ferry: {
                    ...ferry,
                    outward: ferry && ferry.outward ? ferry.outward._id : '',
                    return: ferry && ferry.return ? ferry.return._id : '',
                  },
                })
              } else {
                showFerryPanel()
              }
              setFormState({ formType: FormType.ferry })
            },
          },
          {
            key: 'noFerry',
            text: t('field.ferry.noFerry'),
            onClick: () => {
              handleNoFerry()
            },
          },
          {
            key: 'setFerryStatus',
            text: t('field.status.changeStatus.label'),
            subMenuProps: {
              items: [
                {
                  key: 'ferryBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleFerryBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'ferryBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleFerryBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'ferryBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleFerryBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'ferryBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleFerryBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleNoFerry = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        ferry: {
          outward: null,
          return: null,
          dateTo: null,
          timeTo: null,
          dateFrom: null,
          timeFrom: null,
          bookingStatus: BookingStatus.NotRequired,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })

    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          ferry: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })

      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        ferry: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })
    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          ferry: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })

      selection.setAllSelected(false)
      await refetch()
    }, [selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusFinishedSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        ferry: {
          bookingStatus: BookingStatus.Finished,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })

    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const handleFerrySubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      let newValues: ParticipantUpdateInput = values

      if (
        (newValues.ferry!.bookingStatus === BookingStatus.NotRequired &&
          newValues.ferry!.outward) ||
        (newValues.ferry!.bookingStatus === BookingStatus.NotRequired &&
          newValues.ferry!.return)
      ) {
        newValues!.ferry!.bookingStatus = BookingStatus.Open
      } else {
        newValues = values
      }

      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: { ferry: newValues.ferry },
      }))

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId as any,
          batch,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      await refetch()
      hideFerryPanel()
    },
    [selectedItems, updateParticipants, hideFerryPanel, refetch, selection]
  )

  //#endregion FERRY

  //#region TRACKHOTEL
  useCommand(
    {
      key: 'trackHotel',
      priority: 90,
      text: t('model.proving.participant.trackHotel'),
      disabled: disableControl(),
      iconProps: { iconName: 'RealEstate' },
      subMenuProps: {
        items: [
          {
            key: 'addTrackHotels',
            text: t('field.trackHotel.editTrackHotel'),
            onClick: () => {
              showHotelsPanel({
                trackHotels: {
                  outward: selectedItems[0].trackHotels!.outward!.map(
                    (item: any) => {
                      return {
                        hotel: item.hotel ? item.hotel._id : null,
                        dateFrom: item.dateFrom,
                        dateTo: item.dateTo,
                        bookingStatus: item.bookingStatus,
                      }
                    }
                  ),
                  return: selectedItems[0].trackHotels!.return!.map(
                    (item: any) => {
                      return {
                        hotel: item.hotel ? item.hotel._id : null,
                        dateFrom: item.dateFrom,
                        dateTo: item.dateTo,
                        bookingStatus: item.bookingStatus,
                      }
                    }
                  ),
                },
              })
              setFormState({ formType: FormType.hotels })
            },
          },
          {
            key: 'noTrackHotels',
            text: t('field.trackHotel.noTrackHotel'),
            onClick: () => {
              handleNoTrackHotels()
            },
          },
          {
            key: 'setStatusTrackHotelsOutward',
            text: t('field.trackHotel.setStatusOutward'),
            disabled: selectedItems.length !== 1,
            onClick: () => {
              showHotelsOutwardStatusPanel({
                trackHotels: {
                  outward: selectedItems[0].trackHotels!.outward!.map(
                    (item: any) => {
                      return {
                        hotel: item.hotel ? item.hotel._id : null,
                        dateFrom: item.dateFrom,
                        dateTo: item.dateTo,
                        bookingStatus: item.bookingStatus,
                      }
                    }
                  ),
                },
              })
              setFormState({ formType: FormType.hotelsOutwardStatus })
            },
          },
          {
            key: 'setStatusTrackHotelsReturn',
            text: t('field.trackHotel.setStatusReturn'),
            disabled: selectedItems.length !== 1,
            onClick: () => {
              showHotelsReturnStatusPanel({
                trackHotels: {
                  return: selectedItems[0].trackHotels!.return!.map(
                    (item: any) => {
                      return {
                        hotel: item.hotel ? item.hotel._id : null,
                        dateFrom: item.dateFrom,
                        dateTo: item.dateTo,
                        bookingStatus: item.bookingStatus,
                      }
                    }
                  ),
                },
              })
              setFormState({ formType: FormType.hotelsReturnStatus })
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleNoTrackHotels = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        trackHotels: {
          outward: [],
          return: [],
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: selectedItems[0].provingId as any,
        batch,
      },
    })

    selection.setAllSelected(false)
    await refetch()
  }, [selectedItems, updateParticipants, refetch, selection])

  const addIndexToTrackHotels = (batch: ParticipantBatchInput[]) => {
    batch.forEach((b) => {
      if (b.update && b.update.trackHotels && b.update.trackHotels.outward) {
        b.update.trackHotels.outward.forEach((tho, i) => (tho.index = i + 1))
      }
      if (b.update && b.update.trackHotels && b.update.trackHotels.return) {
        b.update.trackHotels.return.forEach((thr, i) => (thr.index = i + 1))
      }
    })
  }

  const handleHotelsSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)

      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: omitDeep(values, ['id']),
      }))

      addIndexToTrackHotels(batch)

      await updateParticipants({
        variables: {
          provingId: selectedItems[0].provingId! as string,
          batch,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      await refetch()
      hideHotelsPanel()
      hideHotelsOutwardStatusPanel()
      hideHotelsReturnStatusPanel()
    },
    [
      selectedItems,
      updateParticipants,
      hideHotelsPanel,
      refetch,
      selection,
      hideHotelsOutwardStatusPanel,
      hideHotelsReturnStatusPanel,
    ]
  )

  //#endregion TRACKHOTEL
  return (
    <FlexSpinner loading={loading} error={error}>
      <ParticipantListByStartDateNestedGrouping
        // @ts-ignore
        items={itemsT}
        // @ts-ignore
        groups={groupsT}
        selection={selection}
      />

      <FormikPanel {...reassignPanelState} onSubmit={handleReassignSubmit}>
        <ProvingDetailsParticipantReassignForm
          participant={selectedItems[0] || {}}
        />
      </FormikPanel>
      <FormikPanel {...timespanPanelState} onSubmit={handleTimespanSubmit}>
        {formState !== undefined &&
          formState.formType === FormType.timespan && (
            <ProvingDetailsTimespanForm proving={selectedItems[0]} />
          )}
      </FormikPanel>
      <FormikPanel {...updatePanelState} onSubmit={handleUpdateSubmit}>
        {formState !== undefined &&
          formState.formType === FormType.nordschleife && (
            <ProvingDetailsNordschleifeForm />
          )}
      </FormikPanel>
      <FormikPanel
        {...updateOrderIdPanelState}
        onSubmit={handleOrderIdUpdate}
        type={PanelType.largeFixed}
      >
        {formState !== undefined && formState.formType === FormType.orderId && (
          <ProvingDetailsOrderIdForm
            orderIds={
              selectedItems.length !== 0
                ? selectedItems[0].provingOrderIds!
                : []
            }
            // orderIds={proving.orderIdDistribution!.map((x: any) => x)}
          />
        )}
      </FormikPanel>
      <FormikPanel
        {...accomodationPanelState}
        onSubmit={handleAccomodationsSubmit}
        validationSchema={new ProvingDetailsAccomodationFormSchema()}
      >
        {formState !== undefined &&
          selectedItems.length !== 0 &&
          formState.formType === FormType.accomodation && (
            <ProvingDetailsAccomodationForm
              proving={{
                dateFrom: selectedItems[0].provingDateFrom!,
                dateTo: selectedItems[0].provingDateTo!,
                // @ts-ignore as we dont need the rest of the fields
                provingGround: { _id: selectedItems[0]!.provingGroundId! },
              }}
              participant={selectedItems[0]!}
            />
          )}
      </FormikPanel>
      <FormikPanel {...rentalCarPanelState} onSubmit={handleRentalCarSubmit}>
        {formState !== undefined &&
          selectedItems.length !== 0 &&
          formState.formType === FormType.rentalCar && (
            <ProvingDetailsRentalCarForm
              proving={{
                dateFrom: selectedItems[0].provingDateFrom!,
                dateTo: selectedItems[0].provingDateTo!,
                // @ts-ignore as we dont need the rest of the fields
                provingGround: { _id: selectedItems[0]!.provingGroundId! },
              }}
              participant={selectedItems[0]}
            />
          )}
      </FormikPanel>
      <FormikPanel {...ferryPanelState} onSubmit={handleFerrySubmit}>
        {formState !== undefined &&
          selectedItems.length !== 0 &&
          formState.formType === FormType.ferry && (
            <ProvingDetailsFerryForm
              proving={{
                dateFrom: selectedItems[0].provingDateFrom!,
                dateTo: selectedItems[0].provingDateTo!,
                // @ts-ignore as we dont need the rest of the fields
                provingGround: { _id: selectedItems[0]!.provingGroundId! },
              }}
              participant={selectedItems[0]}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...hotelsPanelState}
        onSubmit={handleHotelsSubmit}
        type={PanelType.extraLarge}
        validationSchema={
          new ProvingDetailsTrackHotelFormSchema(
            selectedItems[0]
              ? selectedItems[0].provingDateFrom || ''
              : ('' as string),
            selectedItems[0]
              ? selectedItems[0].provingDateTo || ''
              : ('' as string)
          )
        }
      >
        {formState !== undefined &&
          selectedItems.length !== 0 &&
          formState.formType === FormType.hotels && (
            <ProvingDetailsTrackHotelForm
              proving={{
                dateFrom: selectedItems[0].provingDateFrom!,
                dateTo: selectedItems[0].provingDateTo!,
                // @ts-ignore as we dont need the rest of the fields
                provingGround: { _id: selectedItems[0]!.provingGroundId! },
              }}
              participant={selectedItems[0]}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...hotelsOutwardStatusPanelState}
        onSubmit={handleHotelsSubmit}
        type={PanelType.extraLarge}
      >
        {formState !== undefined &&
          selectedItems.length !== 0 &&
          formState.formType === FormType.hotelsOutwardStatus && (
            <ProvingDetailsTrackHotelOutwardStatusForm
              proving={{
                dateFrom: selectedItems[0].provingDateFrom!,
                dateTo: selectedItems[0].provingDateTo!,
                // @ts-ignore as we dont need the rest of the fields
                provingGround: { _id: selectedItems[0]!.provingGroundId! },
              }}
              participant={selectedItems[0]}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...hotelsReturnStatusPanelState}
        onSubmit={handleHotelsSubmit}
        type={PanelType.extraLarge}
      >
        {formState !== undefined &&
          selectedItems.length !== 0 &&
          formState.formType === FormType.hotelsReturnStatus && (
            <ProvingDetailsTrackHotelReturnStatusForm
              proving={{
                dateFrom: selectedItems[0].provingDateFrom!,
                dateTo: selectedItems[0].provingDateTo!,
                // @ts-ignore as we dont need the rest of the fields
                provingGround: { _id: selectedItems[0]!.provingGroundId! },
              }}
              participant={selectedItems[0]}
            />
          )}
      </FormikPanel>
    </FlexSpinner>
  )
}

export const ParticipantsByStartDateView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.ServiceTeam,
        UserRole.ProvingOrganisation,
        UserRole.Reader,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <ParticipantsByStartDateListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)
